import { SHAPE_DATA_TYPES, type IPageData, type PageDataType } from '../data';
import { DataModel } from './data-model';
import type { IEventManager } from '../manager/event-manager';
import type { DeepPartial, DeepReadonly } from 'ts-essentials';
import type { PageMutationAPI } from '../api/page-mutation-api';
import type { PageStore } from '../store/page-store';
import type { RestrictedDataModel } from './data-model';
import type {
    IDrawingShapeDataModel,
    IGroupShapeDataModel,
    IPlaceholderShapeDataModel,
} from './group-shape-data-model';
import type { PresentationDataModel } from './presentation-data-model';
import type { ITimingDataModel } from './timing-data-model';
import type { IPageContext, PageContext } from '../context/page-context';

export type IPageProps = DeepPartial<DeepReadonly<IPageData>>;

export abstract class PageDataModel<
    Data extends IPageData<PageDataType> = IPageData<PageDataType>,
    Props extends DeepPartial<DeepReadonly<Data>> = DeepPartial<DeepReadonly<Data>>,
> extends DataModel<Data, Props> {
    public abstract context: PageContext;
    public abstract store: PageStore<any, any>;
    public abstract api: PageMutationAPI<any>;

    public constructor(events: IEventManager, public parent: PresentationDataModel) {
        super(events);
    }

    public getPageContext(): IPageContext {
        return this.context;
    }

    public get shapeTree(): IGroupShapeDataModel | undefined {
        return this.store.spTree;
    }

    public get shapeList(): IDrawingShapeDataModel[] {
        return this.shapeTree?.shapeList ?? [];
    }

    public get timing(): ITimingDataModel | undefined {
        return this.store.timing;
    }

    public get placeholders(): IPlaceholderShapeDataModel[] {
        return this.shapeList.filter((shape) => shape.isPlaceholder) as IPlaceholderShapeDataModel[];
    }

    public getShapeById(id: string): IDrawingShapeDataModel | undefined {
        const presentation = this.parent;
        const shape = presentation.store.models.get(id, ...SHAPE_DATA_TYPES);
        if (shape?.getPage().id === this.id) {
            return shape;
        }
    }

    public forEachDescendant(callback: (child: RestrictedDataModel) => void = () => void 0) {
        if (this.shapeTree) {
            this.shapeTree.forEachDescendant((shape) => callback(shape as RestrictedDataModel));
        }
    }

    public detach(): this {
        super.detach();
        this.store.spTree?.detach();
        return this;
    }
}

export type IPageDataModel = RestrictedDataModel<PageDataModel>;
